import React, { useState, useEffect, useCallback } from "react";
import LayoutWrapper from "@iso/components/utility/layoutWrapper.js";
import PageHeader from "@iso/components/utility/pageHeader";
import Box from "@iso/components/utility/box";
import {
  Tabs,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  Switch,
  Popconfirm,
  Space,
  message,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import {
  emergencyResponseProviders,
  emergencyResponseTemplates,
} from "../../../../services/emergencyResponse.service";

const { TabPane } = Tabs;
const { TextArea } = Input;

const ManageEmergencyResponse = () => {
  const [providers, setProviders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [providerModal, setProviderModal] = useState({ open: false, record: null });
  const [templateModal, setTemplateModal] = useState({ open: false, record: null });

  const reload = useCallback(() => {
    setLoading(true);
    Promise.all([
      emergencyResponseProviders.list(),
      emergencyResponseTemplates.list(),
    ])
      .then(([p, t]) => {
        setProviders(p.providers || []);
        setTemplates(t.templates || []);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // ----- Providers -----

  const handleSaveProvider = (values, id) => {
    const op = id
      ? emergencyResponseProviders.update(id, values)
      : emergencyResponseProviders.create(values);
    return op.then((res) => {
      if (res.provider) {
        message.success(id ? "Provider updated" : "Provider created");
        setProviderModal({ open: false, record: null });
        reload();
      } else if (res.errors) {
        message.error("Save failed: " + JSON.stringify(res.errors));
      } else {
        message.error("Save failed");
      }
    });
  };

  const handleDeleteProvider = (id) => {
    emergencyResponseProviders.remove(id).then((res) => {
      if (res.code === "0000") {
        message.success("Provider deleted");
        reload();
      } else {
        message.error(res.message || "Delete failed");
      }
    });
  };

  const providerColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "API URL", dataIndex: "api_url", key: "api_url", render: (v) => v || "—" },
    {
      title: "Auth",
      key: "auth_mode",
      render: (_, r) => {
        if (r.has_oauth_client_secret && r.oauth_token_url && r.oauth_client_id) {
          return r.has_cached_access_token ? "OAuth (cached)" : "OAuth";
        }
        if (r.has_api_key) return "Static key";
        return "—";
      },
    },
    {
      title: "Default Template",
      dataIndex: "default_template_id",
      key: "default_template_id",
      render: (v) => {
        const t = templates.find((x) => x.id === v);
        return t ? t.name : "—";
      },
    },
    {
      title: "Active",
      dataIndex: "is_active",
      key: "is_active",
      render: (v) => (v ? "Yes" : "No"),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setProviderModal({ open: true, record })}
          />
          <Popconfirm
            title="Delete this provider?"
            onConfirm={() => handleDeleteProvider(record.id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ----- Templates -----

  const handleSaveTemplate = (values, id) => {
    const op = id
      ? emergencyResponseTemplates.update(id, values)
      : emergencyResponseTemplates.create(values);
    return op.then((res) => {
      if (res.template) {
        message.success(id ? "Template updated" : "Template created");
        setTemplateModal({ open: false, record: null });
        reload();
      } else if (res.errors) {
        message.error("Save failed: " + JSON.stringify(res.errors));
      } else {
        message.error("Save failed");
      }
    });
  };

  const handleDeleteTemplate = (id) => {
    emergencyResponseTemplates.remove(id).then((res) => {
      if (res.code === "0000") {
        message.success("Template deleted");
        reload();
      } else {
        message.error(res.message || "Delete failed");
      }
    });
  };

  const templateColumns = [
    { title: "Name", dataIndex: "name", key: "name" },
    {
      title: "Body Preview",
      dataIndex: "body",
      key: "body",
      render: (v) => (v || "").slice(0, 80) + ((v || "").length > 80 ? "…" : ""),
    },
    {
      title: "",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => setTemplateModal({ open: true, record })}
          />
          <Popconfirm
            title="Delete this template?"
            onConfirm={() => handleDeleteTemplate(record.id)}
          >
            <Button danger size="small" icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <LayoutWrapper>
      <PageHeader>Manage Emergency Response</PageHeader>
      <Box>
        <Tabs defaultActiveKey="providers">
          <TabPane tab="Providers" key="providers">
            <div style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setProviderModal({ open: true, record: null })}
              >
                Add Provider
              </Button>
            </div>
            <Table
              loading={loading}
              dataSource={providers}
              columns={providerColumns}
              rowKey="id"
              pagination={false}
            />
          </TabPane>
          <TabPane tab="Templates" key="templates">
            <div style={{ marginBottom: 12 }}>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setTemplateModal({ open: true, record: null })}
              >
                Add Template
              </Button>
            </div>
            <Table
              loading={loading}
              dataSource={templates}
              columns={templateColumns}
              rowKey="id"
              pagination={false}
            />
          </TabPane>
        </Tabs>
      </Box>

      <ProviderEditModal
        open={providerModal.open}
        record={providerModal.record}
        templates={templates}
        onCancel={() => setProviderModal({ open: false, record: null })}
        onSubmit={handleSaveProvider}
      />
      <TemplateEditModal
        open={templateModal.open}
        record={templateModal.record}
        onCancel={() => setTemplateModal({ open: false, record: null })}
        onSubmit={handleSaveTemplate}
      />
    </LayoutWrapper>
  );
};

const ProviderEditModal = ({ open, record, templates, onCancel, onSubmit }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(
        record
          ? {
              name: record.name,
              api_url: record.api_url,
              default_template_id: record.default_template_id,
              is_active: !!record.is_active,
              // Static-key field — write-only, blank on edit
              api_key: "",
              // OAuth — token URL + client_id are plain, client_secret
              // is write-only with the same blank-preserves rule.
              oauth_token_url: record.oauth_token_url || "",
              oauth_client_id: record.oauth_client_id || "",
              oauth_client_secret: "",
            }
          : { is_active: true }
      );
    }
  }, [open, record, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      // Strip blank secret fields so backend's "blank = preserve" rule
      // applies. (The non-secret OAuth fields can be safely sent
      // even when empty — admin may want to clear them.)
      const payload = { ...values };
      if (!payload.api_key) delete payload.api_key;
      if (!payload.oauth_client_secret) delete payload.oauth_client_secret;
      onSubmit(payload, record?.id);
    });
  };

  return (
    <Modal
      title={record ? "Edit Provider" : "Add Provider"}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, message: 'Name is required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="api_url" label="API URL">
          <Input placeholder="https://api.example.com/incidents" />
        </Form.Item>
        <Form.Item
          name="api_key"
          label="Static API Key (fallback)"
          extra={
            record
              ? "Leave blank to keep existing. Use this only for static-key providers (Slack-style). For Flytbase, use the OAuth fields below instead."
              : "Optional. Used for static-key providers (Slack-style). For Flytbase use the OAuth fields below."
          }
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 600 }}>OAuth (client credentials)</div>
        <Form.Item
          name="oauth_token_url"
          label="Token URL"
          extra="e.g. https://api.flytbase.com/oauth/token"
        >
          <Input placeholder="https://api.example.com/oauth/token" />
        </Form.Item>
        <Form.Item name="oauth_client_id" label="Client ID">
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="oauth_client_secret"
          label="Client Secret"
          extra={
            record
              ? "Leave blank to keep existing. Changing it clears the cached access + refresh tokens."
              : "Stored server-side; never displayed back."
          }
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>

        <Form.Item name="default_template_id" label="Default Template">
          <Select
            allowClear
            placeholder="No default"
            options={templates.map((t) => ({ label: t.name, value: t.id }))}
          />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </Form>
    </Modal>
  );
};

const TemplateEditModal = ({ open, record, onCancel, onSubmit }) => {
  const [form] = Form.useForm();

  useEffect(() => {
    if (open) {
      form.resetFields();
      form.setFieldsValue(
        record ? { name: record.name, body: record.body } : {}
      );
    }
  }, [open, record, form]);

  const handleOk = () => {
    form.validateFields().then((values) => {
      onSubmit(values, record?.id);
    });
  };

  return (
    <Modal
      title={record ? "Edit Template" : "Add Template"}
      open={open}
      onCancel={onCancel}
      onOk={handleOk}
      destroyOnClose
      width={700}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, whitespace: true, message: 'Name is required' }]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="body"
          label="Body"
          rules={[{ required: true, whitespace: true, message: 'Body is required' }]}
          extra={
            "Supported placeholders: {alarm_id}, {alarm_time}, {triggered_by}, " +
            "{device_name}, {location}, {recipients_with_responses}, " +
            "{termination_reason}, {terminated_by}, {termination_location}"
          }
        >
          <TextArea rows={10} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ManageEmergencyResponse;
